import { useCallback, useEffect, useMemo, useState } from 'react';

import { CloseOutlined, UnorderedListOutlined } from '@ant-design/icons';
import { BankCodesForAll, BANK_KEYWORD_LIST } from '@revenuemarket/bank';
import { Flex } from '@revenuemarket/cds';
import { colorTokens } from '@revenuemarket/design';
import { ResponseStatus } from '@revenuemarket/http';
import { overlays } from '@revenuemarket/overlays';
import { useDeepCopiedState } from '@revenuemarket/react';
import { AGGrid, Toast } from '@revenuemarket/ui';
import {
  ColDef,
  GridApi,
  GridReadyEvent,
  GridSizeChangedEvent,
  ProcessCellForExportParams,
} from 'ag-grid-community';
import { Button, Input, Typography } from 'antd';

import { getBusinessEntityDetail } from '~apis/contract';
import { SearchReceiverAccountListItem } from '~apis/wiring';
import AGGridBankEditor from '~components/ui/AGGrid/editors/AGGridBankEditor';
import AGGridAccountCheckHolderRenderer from '~components/ui/AGGrid/renderers/AGGridAccountCheckHolderRenderer';
import AGGridBankRenderer from '~components/ui/AGGrid/renderers/AGGridBankRenderer';
import AGGridWiringInvoiceRenderer from '~components/ui/AGGrid/renderers/AGGridWiringInvoiceRenderer';
import { useCheckAccountHolder, useGetWiringAccountList } from '~hooks/queries/wiring/query';
import { useCompanyId } from '~hooks/useCompanyId';
import { css } from '~libs/emotion';
import { MIXPANEL_EVENT } from '~libs/mixpanel/event';
import { mixpanel } from '~pages/_app';

import FavoriteAccountSelect from './FavoriteAccountSelect';
import HeaderRenderer from './HeaderRenderer';
import RecentWiringHistoryModal from './RecentWiringHistoryModal';
import WiringAccountCheckBanner from './WiringAccountCheckBanner';
import WiringInfo from './WiringInfo';
import WiringTypeSelector from './WiringTypeSelector';
import { CompressInfo, ReservationInfo, WireListItem, WiringDepositInfoModel } from './types';

function findKeysByKeyword(keyword: string) {
  const matchingKeys = [];

  for (const key in BANK_KEYWORD_LIST) {
    if (BANK_KEYWORD_LIST.hasOwnProperty(key)) {
      const values = BANK_KEYWORD_LIST[key as BankCodesForAll];
      if (values.some((value) => value === keyword)) {
        matchingKeys.push(key);
      }
    }
  }

  return matchingKeys;
}

interface Props {
  model: WiringDepositInfoModel;
  defaultDepositInfo: WireListItem[];
  onBack: (data: WiringDepositInfoModel) => void;
  onFinish: (model: WiringDepositInfoModel) => void;
  defaultTitle?: string;
}

const initialItems = Array.from({ length: 300 }).map((_, index) => {
  return {
    index,
    organizationCode: '',
    accountNumber: '',
    id: index + '',
  };
});

const WiringDepositInfoStep: React.FC<Props> = ({
  model,
  onFinish,
  onBack,
  defaultDepositInfo,
  defaultTitle,
}) => {
  const items = useDeepCopiedState(initialItems) as unknown as (WireListItem & { id: number })[];
  const { companyId } = useCompanyId();
  const [gridApi, setGridApi] = useState<GridApi>();
  const [totalAmount, setTotalAmount] = useState(0);

  const [title, setTitle] = useState(defaultTitle ?? '');

  const [reservationInfo, setReservationInfo] = useState<ReservationInfo>({
    isReservation: model.reservationInfo.isReservation,
    reservationDate: model.reservationInfo.reservationDate,
    reservationTime: model.reservationInfo.reservationTime,
  });
  const [isPayrollMode, setIsPayrollMode] = useState(model?.isPayroll);

  // 압축기장여부
  const [compressInfo, setCompressInfo] = useState<CompressInfo>({
    isCompress: model.compressInfo.isCompress,
    compressMemo: model.compressInfo.compressMemo?.replace('_', ''),
  });
  const { mutateAsync: checkAccountHolderRequest } = useCheckAccountHolder();
  const preFilledData = model?.depositInfo.length ? model?.depositInfo : defaultDepositInfo;

  useEffect(() => {
    const getBusinessEntityInfo = async (data: WireListItem, index: number) => {
      if (!data.businessEntityId) return;
      const response = getBusinessEntityDetail({ companyId, id: data.businessEntityId });
      const target = (await response).Data?.data?.bankAccounts?.[0];
      gridApi?.getRowNode(index + '')?.setData({
        ...data,
        organizationCode: target?.organizationCode ?? preFilledData[index].organizationCode,
        accountNumber: target?.accountNumber ?? preFilledData[index].accountNumber,
        businessEntity: (await response).Data?.data?.name ?? '',
      });

      if (target?.organizationCode && target?.accountNumber) {
        checkAccountHolder({
          selectedRowIndex: index,
          organizationCode: target.organizationCode,
          accountNumber: target.accountNumber,
          businessRegNo: data.businessRegNo,
        });
      }
    };
    if (preFilledData && gridApi && items.length > 0) {
      preFilledData.forEach((data, index) => {
        if (data.businessEntityId) {
          getBusinessEntityInfo(data, index);
        } else {
          gridApi?.getRowNode(index + '')?.setData({
            ...data,
          });

          if (data?.organizationCode && data?.accountNumber) {
            checkAccountHolder({
              selectedRowIndex: index,
              organizationCode: data.organizationCode,
              accountNumber: data.accountNumber,
            });
          }
        }
      });
    }
  }, [preFilledData, gridApi, items]);

  const onGridReady = useCallback(({ api }: GridReadyEvent) => {
    setGridApi(api);
    api.sizeColumnsToFit();
  }, []);

  // 예금주 확인
  const checkAccountHolder = async (data: {
    selectedRowIndex: number;
    organizationCode: BankCodesForAll;
    accountNumber: string;
    businessRegNo?: string;
  }) => {
    if (data?.businessRegNo) {
      checkBusinessEntityAccount(data);
      return;
    }

    const selectedRow = gridApi?.getRowNode(data.selectedRowIndex.toString());

    if (!selectedRow) return;

    const response = await checkAccountHolderRequest({
      companyId,
      request: {
        organizationCode: data.organizationCode,
        bankAccountNo: data.accountNumber,
      },
    });

    if (response.Status === ResponseStatus.SUCCESS) {
      selectedRow.setData({
        ...selectedRow.data,
        accountCheckedHolder: response.Data?.data.holderName,
        accountCheckStatus: 'CHECKED',
      });
      gridApi?.refreshCells({ rowNodes: [selectedRow] });
    } else {
      selectedRow.setData({
        ...selectedRow.data,
        accountCheckedHolder: null,
        accountCheckStatus: response.ErrorCode === 'ACCOUNT_NOT_FOUND' ? 'FAILED' : 'RETRY',
      });
      gridApi?.refreshCells({ rowNodes: [selectedRow] });
    }
  };

  // 거래처 계좌 확인
  const checkBusinessEntityAccount = async (data: {
    selectedRowIndex: number;
    organizationCode: BankCodesForAll;
    accountNumber: string;
    businessRegNo?: string;
  }) => {
    const selectedRow = gridApi?.getRowNode(data.selectedRowIndex.toString());

    if (!selectedRow) return;

    const response = await checkAccountHolderRequest({
      companyId,
      request: {
        organizationCode: data.organizationCode,
        bankAccountNo: data.accountNumber,
        businessRegNo: data.businessRegNo,
      },
    });

    if (response.Status === ResponseStatus.SUCCESS) {
      selectedRow.setData({
        ...selectedRow.data,
        accountCheckedHolder: response.Data?.data.holderName,
        accountCheckStatus: response.Data?.data.identified ? 'CHECKED' : 'WARNING',
      });
    } else {
      selectedRow.setData({
        ...selectedRow.data,
        accountCheckedHolder: null,
        accountCheckStatus: response.ErrorCode === 'ACCOUNT_NOT_FOUND' ? 'FAILED' : 'RETRY',
      });
    }
  };

  const columnDefs = useMemo<ColDef<WireListItem>[]>(
    () =>
      gridApi
        ? [
            {
              headerName: '예금주 확인',
              field: 'accountCheckStatus',
              cellClass: css`
                background: rgba(0, 0, 0, 0.06);
              `,
              cellRenderer: AGGridAccountCheckHolderRenderer,
              cellRendererParams: {
                onRetry: checkAccountHolder,
              },
            },
            {
              headerName: '거래처 확인',
              field: 'businessEntity',
              cellClass: css`
                background: rgba(0, 0, 0, 0.06);
                display: flex;
                align-items: center;
              `,
            },
            {
              headerName: '은행',
              field: 'organizationCode',
              editable: true,
              cellRenderer: AGGridBankRenderer,
              cellEditor: AGGridBankEditor,
              singleClickEdit: true,
              suppressKeyboardEvent(params) {
                return params.editing && params.event.key === 'Enter';
              },
              onCellValueChanged(event) {
                if (event.data.accountNumber && event.data.organizationCode) {
                  checkAccountHolder({
                    selectedRowIndex: event.node?.rowIndex ?? -1,
                    organizationCode: event.data.organizationCode,
                    accountNumber: event.data.accountNumber,
                    businessRegNo: event.data.businessRegNo,
                  });
                }
              },
            },
            {
              headerName: '계좌',
              field: 'accountNumber',
              editable: true,
              cellStyle: {
                display: 'flex',
                alignItems: 'center',
              },
              onCellValueChanged(event) {
                if (event.data.accountNumber && event.data.organizationCode) {
                  checkAccountHolder({
                    selectedRowIndex: event.node?.rowIndex ?? -1,
                    organizationCode: event.data.organizationCode,
                    accountNumber: event.data.accountNumber,
                    businessRegNo: event.data.businessRegNo,
                  });
                }
              },
              valueParser: (params) => {
                return params.newValue.replace(/[^0-9]/g, '');
              },
            },
            {
              headerName: '금액',
              field: 'amount',
              cellClass: css`
                justify-content: flex-end;
              `,
              cellStyle: {
                display: 'flex',
                alignItems: 'center',
              },
              headerComponentParams: {
                align: 'right',
              },
              editable: true,
              cellEditor: 'agNumberCellEditor',
              valueFormatter: (params) => {
                return params.value ? `${params.value.toLocaleString()}` : '';
              },
              onCellValueChanged(event) {
                setTotalAmount(
                  items
                    .map((item) => gridApi?.getRowNode(item?.id)?.data as WireListItem)
                    .reduce((acc, cur) => acc + (cur?.amount ?? 0), 0),
                );
              },
            },

            {
              headerName: '내 통장 적요',
              field: 'senderDescription',
              cellStyle: {
                display: 'flex',
                alignItems: 'center',
              },
              width: 250,
              headerComponent: HeaderRenderer,
              headerComponentParams: {
                alertText: `(${model.wiringBank === 'HANA' ? 15 : 14}자 이내)`,
              },
              valueParser: (params) => {
                const maxLength = model.wiringBank === 'HANA' ? 15 : 14;
                if (params.newValue.length > maxLength) {
                  // Show a warning or limit input
                  Toast.error(`입력된 내용은 최대 ${maxLength}자를 초과할 수 없습니다.`);
                  return params.newValue.slice(0, maxLength); // Reject new value
                }
                return params.newValue; // Accept new value
              },
              editable: true,
            },
            {
              headerName: '받는 분 적요',
              width: 250,
              headerComponent: HeaderRenderer,
              headerComponentParams: {
                alertText: '(7자 이내)',
              },
              valueParser: (params) => {
                if (params.newValue.length > 7) {
                  // Show a warning or limit input
                  Toast.error('입력된 내용은 최대 7자를 초과할 수 없습니다.');
                  return params.newValue.slice(0, 7); // Reject new value
                }
                return params.newValue; // Accept new value
              },
              cellStyle: {
                display: 'flex',
                alignItems: 'center',
              },
              field: 'receiverDescription',
              editable: true,
            },
            {
              headerName: '메모',
              cellStyle: {
                display: 'flex',
                alignItems: 'center',
              },
              field: 'memo',
              editable: true,
            },
            {
              headerName: '증빙',
              // 특정 조건일때 column을 안보여주고 싶을때
              hide: defaultDepositInfo.some((item) => item.taxInvoiceId) ? false : true,
              cellStyle: {
                display: 'flex',
                alignItems: 'center',
              },
              cellRenderer: AGGridWiringInvoiceRenderer,
            },
            {
              headerName: '',
              width: 60,
              cellStyle: {
                display: 'flex',
                alignItems: 'center',
              },
              cellRenderer: () => {
                return (
                  <Button
                    type="text"
                    onClick={() => {
                      // 삭제 버튼 클릭 시 해당 row 삭제
                      const selectedRowIndex = gridApi?.getFocusedCell()?.rowIndex ?? -1;

                      const selectedRow = gridApi?.getRowNode(selectedRowIndex.toString());
                      if (!selectedRow) return;

                      selectedRow.setData({
                        organizationCode: '',
                        accountNumber: '',
                        amount: 0,
                        senderDescription: '',
                        receiverDescription: '',
                        memo: '',
                        accountCheckedHolder: '',
                        accountCheckStatus: undefined,
                        businessEntity: '',
                        businessEntityId: undefined,
                      });
                    }}
                    icon={
                      <CloseOutlined
                        style={{
                          color: colorTokens.iconDefault,
                        }}
                      />
                    }
                  />
                );
              },
            },
          ]
        : [],
    [items, gridApi, defaultDepositInfo],
  );

  const onAccountSelect = (accountInfo: SearchReceiverAccountListItem) => {
    // organizationCode, accountNumber가 비어있는 row 찾기
    const emptyRowIndex = items
      .map((item) => gridApi?.getRowNode(item.id)?.data as WireListItem)
      .findIndex((data) => {
        return (
          !data?.organizationCode &&
          !data?.accountNumber &&
          !data?.amount &&
          !data?.senderDescription &&
          !data?.receiverDescription &&
          !data?.memo
        );
      });

    const emptyRow = gridApi?.getRowNode(emptyRowIndex + '');

    if (!emptyRow) return;
    // 찾은 row에 데이터 채우기
    emptyRow.setData({
      ...emptyRow.data,
      businessEntityId: accountInfo.businessEntityId,
      organizationCode: accountInfo.bankCode,
      accountNumber: accountInfo.accountNumber,
    });

    // checkAccountHolder
    checkAccountHolder({
      selectedRowIndex: emptyRow.rowIndex ?? -1,
      organizationCode: accountInfo.bankCode,
      accountNumber: accountInfo.accountNumber,
      businessRegNo: accountInfo.businessEntityRegNo,
    });
  };

  const failItemExist = items
    .map((item) => gridApi?.getRowNode(item.id)?.data as WireListItem)
    ?.some((data) => {
      return data?.accountCheckStatus === 'FAILED';
    });

  const warnItemExist = items
    .map((item) => gridApi?.getRowNode(item.id)?.data as WireListItem)
    ?.some((data) => {
      return data?.accountCheckStatus === 'WARNING';
    });

  const depositInfo =
    items
      .map((item) => gridApi?.getRowNode(item.id)?.data as WireListItem)
      ?.filter((item) => item?.organizationCode && item?.accountNumber) || [];

  const checkFinished = depositInfo?.some((info) => {
    return info.organizationCode && info.accountNumber && info.amount;
  });

  const { data: accountListData } = useGetWiringAccountList({ companyId });
  const accountList = accountListData?.Data?.data?.items || [];

  const selectedAccount = accountList.find((account) => account.accountId === model.accountInfoId);

  const validatePrice = totalAmount > (selectedAccount?.accountBalance ?? 0);

  const processCellFromClipboard = useCallback((e: ProcessCellForExportParams<WireListItem>) => {
    switch (e.column.getColDef().field) {
      case 'amount':
        return e.value ? Number(e.value.replace(/,/g, '')) : '';

      case 'accountNumber':
        return e.value.replace(/[^0-9]/g, '');
      case 'organizationCode':
        if (!isNaN(Number(e.value))) {
          return e.value.length === 3
            ? e.value
            : e.value.length === 2
              ? '0' + e.value
              : '00' + e.value;
        }
        const keyword = findKeysByKeyword(e.value)[0] ? findKeysByKeyword(e.value)[0] : e.value;

        return keyword;
      default:
        return e.value;
    }
  }, []);

  const processCellForClipboard = useCallback((e: ProcessCellForExportParams<WireListItem>) => {
    switch (e.column.getColDef().field) {
      case 'amount':
        return e.value ? Number(e.value) : '';
      case 'organizationCode':
        return BANK_KEYWORD_LIST[e.value as BankCodesForAll][0];
      default:
        return e.value;
    }
  }, []);

  const handleFitGridSize = useCallback(({ api }: { api: GridApi }) => {
    api.sizeColumnsToFit();
  }, []);

  const onGridSizeChanged = useCallback(({ api }: GridSizeChangedEvent) => {
    api.sizeColumnsToFit();
  }, []);

  useEffect(() => {
    return () => {
      if (gridApi) {
        gridApi.destroy();
      }
    };
  }, []);

  return (
    <Flex direction="column" gap={24}>
      <Flex
        direction="column"
        gap={24}
        style={{
          maxHeight: 'calc(100vh - 230px)',
          overflowY: 'scroll',
        }}
      >
        <Flex direction="column" gap={8}>
          <Flex align="center" gap={4}>
            <Typography.Text
              style={{
                fontSize: 14,
              }}
            >
              이체명
            </Typography.Text>
          </Flex>
          <Input
            value={title}
            onChange={(e) => {
              setTitle(e.target.value);
            }}
            placeholder="이체명을 입력하세요"
          />
        </Flex>
        <Flex direction="column" gap={8}>
          <Typography
            style={{
              fontSize: 14,
            }}
          >
            입금 정보
          </Typography>
          <Flex
            style={{
              padding: '10px 16px',
              background: colorTokens.bgPrimary,
            }}
            gap={8}
            align="center"
          >
            <Typography>
              <b>엑셀처럼 입력하세요.</b> 셀을 클릭하고 직접 입력하거나, 여러 셀을 드래그하여 한번에
              복사+붙여넣기할 수 있어요.{' '}
            </Typography>
          </Flex>
          <Flex align="center" gap={16}>
            <FavoriteAccountSelect onAccountSelect={onAccountSelect} selectedList={depositInfo} />

            <Button
              icon={
                <UnorderedListOutlined
                  style={{
                    color: colorTokens.iconDefault,
                  }}
                />
              }
              onClick={() => {
                overlays.open(({ isOpen, onClose }) => (
                  <RecentWiringHistoryModal
                    open={isOpen}
                    onClose={onClose}
                    onCancel={onClose}
                    onComplete={(completeItems) => {
                      completeItems.forEach((item, index) => {
                        const emptyRowIndex = items
                          .map((item) => gridApi?.getRowNode(item.id)?.data as WireListItem)
                          .find((data) => {
                            return (
                              !data?.organizationCode &&
                              !data?.accountNumber &&
                              !data?.amount &&
                              !data?.senderDescription &&
                              !data?.receiverDescription &&
                              !data?.memo
                            );
                          });

                        const emptyRow = gridApi?.getRowNode(emptyRowIndex?.id + '');

                        if (!emptyRow) return;

                        emptyRow.setData({
                          ...emptyRow.data,
                          memo: item.memo,
                          senderDescription: item.senderDescription,
                          receiverDescription: item.receiverDescription,
                          organizationCode: item.receiverBankCode,
                          accountNumber: item.receiverAccountNumber,
                          amount: item.amount,
                        });
                        // checkAccountHolder
                        checkAccountHolder({
                          selectedRowIndex: emptyRow.rowIndex ?? -1,
                          organizationCode: item.receiverBankCode,
                          accountNumber: item.receiverAccountNumber,
                        });
                      });

                      onClose();
                    }}
                  />
                ));
              }}
            >
              최근 이체내역 불러오기
            </Button>
          </Flex>
          <Flex
            className={css`
              height: 100%;
              flex: 1;
            `}
          >
            <AGGrid
              containerStyle={{
                height: '30vh',
              }}
              className={css`
                .ag-root-wrapper {
                  border-left: none;
                  border-right: none;
                  border-radius: 0;
                }
              `}
              onGridSizeChanged={onGridSizeChanged}
              onDisplayedColumnsChanged={handleFitGridSize}
              stopEditingWhenCellsLoseFocus
              processCellFromClipboard={processCellFromClipboard}
              processCellForClipboard={processCellForClipboard}
              id="wiring"
              enableFillHandle
              enableRangeHandle
              enableRangeSelection
              fillHandleDirection="y"
              rowSelection="multiple"
              onGridReady={onGridReady}
              columnDefs={columnDefs}
              rowData={items}
              headerHeight={32}
              rowHeight={56}
            />
          </Flex>
        </Flex>

        <Flex direction="column" gap={8}>
          {failItemExist && <WiringAccountCheckBanner status="FAILED" />}
          {warnItemExist && <WiringAccountCheckBanner status="WARNING" />}
          <WiringInfo
            info={{
              id: model.accountInfoId,
              targetPrice: depositInfo.reduce((acc, cur) => acc + (cur?.amount ?? 0), 0),
              itemListLength: depositInfo.length,
            }}
          />
          <WiringTypeSelector
            selectedBank={model?.wiringBank}
            reservationInfo={reservationInfo}
            isPayrollMode={isPayrollMode}
            compressInfo={compressInfo}
            setCompressInfo={setCompressInfo}
            setIsPayrollMode={setIsPayrollMode}
            setReservationInfo={setReservationInfo}
          />
        </Flex>
      </Flex>
      <Flex justify="flex-end" gap={8} align="center">
        <Button
          onClick={() =>
            onBack({
              ...model,
              title: title,
              depositInfo: depositInfo || [],
            })
          }
        >
          이전
        </Button>
        <Button
          type="primary"
          disabled={!checkFinished}
          onClick={() => {
            mixpanel.track(MIXPANEL_EVENT.WIRING.REQUEST_WIRE_TRANSFER);
            mixpanel.track(MIXPANEL_EVENT.ENGAGEMENT.DEFAULT, {
              type: 'request_wire_transfer',
            });
            onFinish({
              ...model,
              title: title,
              isPayroll: isPayrollMode,
              reservationInfo,
              compressInfo,
              depositInfo: depositInfo,
            });
          }}
        >
          다음
        </Button>
      </Flex>
    </Flex>
  );
};

export default WiringDepositInfoStep;
